import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getPersonnel, startPersonnelResearch, pollPersonnelStatus, reextractPersonnel } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';
import Disclaimer from '../components/Disclaimer';

const INFLUENCE_COLOR = {
  'Nhà lãnh đạo ngành':  '#10b981',
  'Chuyên gia hàng đầu': '#3b82f6',
  'Chuyên gia':          '#8b5cf6',
  'Contributor':         '#f59e0b',
  'Chưa xác định':       '#475569',
};
const SOURCE_ICON = {
  linkedin:  '🔗',
  twitter:   '𝕏',
  github:    '💻',
  academic:  '📄',
  news:      '📰',
  website:   '🌐',
  search:    '🔍',
  other:     '📌',
};

function ScoreGauge({ label, score, color }) {
  const pct = score ? Math.round((score / 10) * 100) : 0;
  const r = 28, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={sg.wrap}>
      <svg width={72} height={72} viewBox="0 0 72 72">
        <circle cx={36} cy={36} r={r} fill="none" stroke="var(--border)" strokeWidth={6} />
        <circle cx={36} cy={36} r={r} fill="none"
          stroke={color || '#3b82f6'} strokeWidth={6}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 36 36)"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
        <text x={36} y={38} textAnchor="middle" fontSize={13} fontWeight={700}
          fill={color || '#3b82f6'}>{score ? score.toFixed(1) : '—'}</text>
      </svg>
      <div style={sg.label}>{label}</div>
    </div>
  );
}
const sg = {
  wrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  label: { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', textAlign: 'center' },
};

function ReportSection({ title, content }) {
  if (!content) return null;
  return (
    <div style={rs.wrap}>
      <div style={rs.title}>{title}</div>
      <div style={rs.body}
        dangerouslySetInnerHTML={{ __html: formatMarkdown(content) }}
      />
    </div>
  );
}
const rs = {
  wrap: { marginBottom: 24, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' },
  title: { fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid var(--border)', paddingBottom: 8 },
  body: { fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.8, wordBreak: 'break-word', overflowWrap: 'break-word' },
};

// Simple markdown to HTML (bold, italic, links, lists)
function formatMarkdown(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text)">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer" style="color:var(--accent);text-decoration:underline">$1</a>')
    .replace(/^- (.+)$/gm, '<div style="margin:4px 0;padding-left:12px;border-left:2px solid var(--border-mid)">$1</div>')
    .replace(/\n\n+/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

function parseReportSections(report) {
  if (!report) return {};
  const clean = report
    .replace(/^```[^\n]*\n?/gm, '').replace(/```\s*$/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, (_, t) => `**${t}**`);

  const sections = {};
  let current = null, buf = [];
  for (const line of clean.split('\n')) {
    const m = line.match(/^## (\d+)\.\s+(.+)/);
    if (m) {
      if (current) sections[current] = buf.join('\n').trim();
      current = `sec${m[1]}`;
      buf = [];
    } else {
      buf.push(line);
    }
  }
  if (current) sections[current] = buf.join('\n').trim();
  return sections;
}

export default function PersonnelDetailPage({ slug, onBack }) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [person, setPerson]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [researching, setRes]     = useState(false);
  const [reextracting, setReex]   = useState(false);
  const [cooldown, setCooldown]   = useState(null); // { message, nextResearchDate }
  const pollingRef = useRef(null);

  const load = useCallback(async () => {
    try { const p = await getPersonnel(slug); setPerson(p); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Poll while running/pending
  useEffect(() => {
    if (!person) return;
    if (person.status === 'running' || person.status === 'pending') {
      pollingRef.current = setInterval(async () => {
        try {
          const st = await pollPersonnelStatus(slug);
          if (st.status === 'completed' || st.status === 'failed') {
            clearInterval(pollingRef.current);
            setRes(false);
            await load();
          }
        } catch { clearInterval(pollingRef.current); }
      }, 4000);
    }
    return () => clearInterval(pollingRef.current);
  }, [person?.status, slug, load]);

  const handleStartResearch = async () => {
    setCooldown(null);
    setRes(true);
    try {
      await startPersonnelResearch(slug);
      await load();
    } catch (e) {
      if (e.cooldown) {
        setCooldown({ message: e.message, nextResearchDate: e.nextResearchDate });
      } else if (e.insufficientCredits) {
        alert(e.message);
      } else if (e.premiumRequired) {
        alert(t('personnelDetail.errAdvancedPremium'));
      } else {
        alert(t('personnelDetail.errPrefix') + e.message);
      }
      setRes(false);
    }
  };

  const handleReextract = async () => {
    setReex(true);
    try {
      await reextractPersonnel(slug);
      await load();
    } catch (e) {
      alert(t('personnelDetail.errExtractFailed') + e.message);
    } finally {
      setReex(false);
    }
  };

  if (loading) return <div style={s.loadingPage}>{t('personnelDetail.loadingProfile')}</div>;
  if (!person) return <div style={s.loadingPage}>{t('personnelDetail.profileNotFound')}</div>;

  const isRunning = person.status === 'running' || person.status === 'pending';
  const isDone    = person.status === 'completed';
  const sections  = parseReportSections(person.report);
  const infColor  = INFLUENCE_COLOR[person.influenceLevel] || '#475569';

  const avgScore = isDone
    ? ((person.influenceScore || 0) + (person.credibilityScore || 0) + (person.trustScore || 0)) / 3
    : null;

  const mobileStyle = isMobile ? {
    page: { ...s.page, padding: '16px 12px' },
    columns: { ...s.columns, flexDirection: 'column' },
    leftCol: { ...s.leftCol, width: '100%' },
  } : {};

  return (
    <div style={{ ...s.page, ...mobileStyle.page }}>
      {/* Back button */}
      <button style={s.backBtn} onClick={onBack}>← {t('personnelDetail.backToList')}</button>

      {/* ── Header card ── */}
      <div style={s.headerCard}>
        <div style={s.headerLeft}>
          <Avatar name={person.name} size={72} />
          <div style={{ minWidth: 0 }}>
            <div style={s.heroName}>{person.name}</div>
            <div style={s.heroRole}>{person.role}</div>
            <div style={s.heroProject}>
              <span style={{ ...s.projBadge, borderColor: 'rgba(31,88,164,0.3)', color: 'var(--accent)' }}>
                📊 {person.project}
              </span>
              {person.influenceLevel && (
                <span style={{ ...s.projBadge, borderColor: infColor + '40', color: infColor }}>
                  {person.influenceLevel}
                </span>
              )}
              {person.personnelGroup && (
                <span style={{ ...s.projBadge, borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                  {person.personnelGroup}
                </span>
              )}
            </div>
          </div>
        </div>

        {isDone && avgScore && (
          <div style={s.headerRight}>
            <ScoreGauge label={t('personnelDetail.scoreInfluence')} score={person.influenceScore}
              color={person.influenceScore >= 7 ? '#10b981' : person.influenceScore >= 4 ? '#f59e0b' : '#ef4444'} />
            <ScoreGauge label={t('personnelDetail.scoreCredibility')} score={person.credibilityScore}
              color={person.credibilityScore >= 7 ? '#3b82f6' : person.credibilityScore >= 4 ? '#f59e0b' : '#ef4444'} />
            <ScoreGauge label={t('personnelDetail.scoreTrust')} score={person.trustScore}
              color={person.trustScore >= 7 ? '#8b5cf6' : person.trustScore >= 4 ? '#f59e0b' : '#ef4444'} />
            <div style={s.overallWrap}>
              <div style={{ ...s.overallBadge,
                background: (RATING_COLOR[person.overallRating] || '#475569') + '20',
                color: RATING_COLOR[person.overallRating] || '#475569',
                borderColor: (RATING_COLOR[person.overallRating] || '#475569') + '40',
              }}>
                {person.overallRating || '—'}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{t('personnelDetail.overallRating')}</div>
            </div>
          </div>
        )}
      </div>

      {/* Red flags */}
      {person.redFlags?.length > 0 && (
        <div style={s.redFlagBar}>
          <span style={s.redFlagTitle}>⚠️ {t('personnelDetail.warningFlags')}:</span>
          {person.redFlags.map((f, i) => (
            <span key={i} style={s.redFlagItem}>{f}</span>
          ))}
        </div>
      )}

      <div style={{ ...s.columns, ...mobileStyle.columns }}>
        {/* ── Left: Basic info + actions ── */}
        <div style={{ ...s.leftCol, ...mobileStyle.leftCol }}>
          {/* Basic info (from project research) */}
          <div style={s.infoCard}>
            <div style={s.infoTitle}>📋 {t('personnelDetail.basicInfo')}</div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>{t('personnelDetail.role')}</span>
              <span style={s.infoVal}>{person.role || '—'}</span>
            </div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>{t('personnelDetail.project')}</span>
              <span style={{ ...s.infoVal, color: 'var(--accent)' }}>{person.project || '—'}</span>
            </div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>{t('personnelDetail.preliminaryRating')}</span>
              <span style={s.infoVal}>{person.basicCredibility || '—'}</span>
            </div>
            {person.basicBackground && (
              <div style={s.bgBox}>
                <div style={s.infoLabel}>{t('personnelDetail.backgroundFromProject')}</div>
                <p style={s.bgText}>{person.basicBackground}</p>
              </div>
            )}
          </div>

          {/* Research action */}
          <div style={s.actionCard}>
            <div style={s.actionTitle}>🔬 {t('personnelDetail.advancedResearch')}</div>
            <p style={s.actionDesc}>{t('personnelDetail.advancedResearchDesc')}</p>
            {person.status === 'none' || person.status === 'failed' ? (
              <>
                {person.status === 'failed' && (
                  <div style={s.errMsg}>⚠️ {person.error || t('personnelDetail.researchFailed')}</div>
                )}
                {person.status === 'failed' && person.report && (
                  <button style={s.reextractBtn} onClick={handleReextract} disabled={reextracting}>
                    {reextracting ? t('personnelDetail.extracting') : t('personnelDetail.reextractFromReport')}
                  </button>
                )}
                <button style={s.researchBtn} onClick={handleStartResearch} disabled={researching}>
                  {researching ? t('personnelDetail.starting') : t('personnelDetail.startAdvancedResearch')}
                </button>
              </>
            ) : isRunning ? (
              <div style={s.runningBox}>
                <div style={s.runningSpinner} />
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{t('personnelDetail.aiResearching')}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{t('personnelDetail.usuallyTakes')}</div>
                </div>
              </div>
            ) : isDone ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={s.doneBox}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div>
                    <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600 }}>{t('personnelDetail.completed')}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {person.completedAt ? new Date(person.completedAt).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'vi-VN') : ''}
                    </div>
                  </div>
                  <button style={s.rerunBtn} onClick={handleStartResearch} disabled={researching}>
                    🔄 {t('personnelDetail.rerun')}
                  </button>
                </div>
                {cooldown && (
                  <div style={s.cooldownBox}>
                    <span style={{ fontSize: 14 }}>🕐</span>
                    <div>
                      <div style={{ fontSize: 11, color: '#fbbf24', fontWeight: 700 }}>{t('personnelDetail.cannotRerunYet')}</div>
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{cooldown.message}</div>
                    </div>
                  </div>
                )}
                {!person.influenceScore && !person.credibilityScore && !person.trustScore && person.report && (
                  <button style={s.reextractBtn} onClick={handleReextract} disabled={reextracting}>
                    {reextracting ? t('personnelDetail.extracting') : t('personnelDetail.reextractScores')}
                  </button>
                )}
              </div>
            ) : null}
          </div>

          {/* Sources */}
          {isDone && person.sources?.length > 0 && (
            <div style={s.sourcesCard}>
              <div style={s.infoTitle}>🔗 {t('personnelDetail.sources', { count: person.sources.length })}</div>
              {person.sources.map((src, i) => (
                <a key={i} href={src.url} target="_blank" rel="noreferrer" style={s.sourceItem}>
                  <span style={s.srcIcon}>{SOURCE_ICON[src.type] || '📌'}</span>
                  <div style={s.srcContent}>
                    <div style={s.srcTitle}>{src.title}</div>
                    <div style={s.srcUrl}>{src.url.replace(/^https?:\/\//, '').slice(0, 50)}{src.url.length > 53 ? '…' : ''}</div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: Full report ── */}
        <div style={s.rightCol}>
          {!isDone && !isRunning && (
            <div style={s.noReport}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, marginBottom: 8 }}>{t('personnelDetail.noAdvancedReport')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('personnelDetail.noReportHint')}</div>
            </div>
          )}

          {isRunning && (
            <div style={s.noReport}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
              <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, marginBottom: 8 }}>{t('personnelDetail.aiResearching')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('personnelDetail.resultWillAppear')}</div>
            </div>
          )}

          {isDone && (
            <>
              <ReportSection title={`1. ${t('personnelDetail.sec1')}`} content={sections.sec1} />
              <ReportSection title={`2. ${t('personnelDetail.sec2')}`} content={sections.sec2} />
              <ReportSection title={`3. ${t('personnelDetail.sec3')}`} content={sections.sec3} />
              <ReportSection title={`4. ${t('personnelDetail.sec4')}`} content={sections.sec4} />
              <ReportSection title={`5. ${t('personnelDetail.sec5')}`} content={sections.sec5} />
              <ReportSection title={`6. ${t('personnelDetail.sec6')}`} content={sections.sec6} />
              <ReportSection title={`7. ${t('personnelDetail.sec7')}`} content={sections.sec7} />
              <ReportSection title={`8. ${t('personnelDetail.sec8')}`} content={sections.sec8} />
              <ReportSection title={`9. ${t('personnelDetail.sec9')}`} content={sections.sec9} />
              <ReportSection title={`10. ${t('personnelDetail.sec10')}`} content={sections.sec10} />
              <ReportSection title={`11. ${t('personnelDetail.sec11')}`} content={sections.sec11} />
            </>
          )}
        </div>
      </div>

      <Disclaimer />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        a[href]:hover { opacity: 0.8; }
      `}</style>
    </div>
  );
}

function Avatar({ name, size = 52 }) {
  const parts = (name || '').trim().split(/\s+/);
  const init = parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : (name || '?').slice(0, 2);
  const colors = ['#10b981','#3b82f6','#8b5cf6','#f59e0b','#ec4899','#06b6d4','#ef4444','#14b8a6'];
  const bg = colors[((name || '').charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.28, flexShrink: 0,
      background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.33, fontWeight: 800, color: '#fff', letterSpacing: -1 }}>
      {init.toUpperCase()}
    </div>
  );
}

const RATING_COLOR = {
  'Xuất sắc':         '#10b981',
  'Tốt':              '#3b82f6',
  'Trung bình':       '#f59e0b',
  'Yếu':              '#ef4444',
  'Chưa đủ dữ liệu':  '#475569',
};

const s = {
  page: { padding: '20px 32px', maxWidth: 1200, margin: '0 auto', minHeight: '80vh' },
  loadingPage: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: 'var(--text-muted)', fontSize: 14 },
  backBtn: {
    background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, padding: '4px 0', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6,
  },

  headerCard: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 28px',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 24, flexWrap: 'wrap',
    boxShadow: '0 2px 8px rgba(31,88,164,0.07)',
  },
  headerLeft: { display: 'flex', alignItems: 'flex-start', gap: 20, flex: 1, minWidth: 0 },
  heroName: { fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 4, wordBreak: 'break-word', overflowWrap: 'break-word' },
  heroRole: { fontSize: 13, color: 'var(--text-muted)', marginBottom: 10, wordBreak: 'break-word', overflowWrap: 'break-word' },
  heroProject: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  projBadge: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, border: '1px solid' },
  headerRight: { display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' },
  overallWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  overallBadge: { fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 20, border: '1px solid' },

  redFlagBar: {
    background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 10,
    padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
  },
  redFlagTitle: { fontSize: 12, fontWeight: 700, color: '#dc2626' },
  redFlagItem: { fontSize: 11, color: '#b91c1c', background: 'rgba(220,38,38,0.1)', padding: '2px 8px', borderRadius: 8 },

  columns: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  leftCol: { width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  rightCol: { flex: 1, minWidth: 0 },

  infoCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 },
  infoTitle: { fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 12 },
  infoRow: { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 },
  infoLabel: { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 100, paddingTop: 2 },
  infoVal: { fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' },
  bgBox: { marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' },
  bgText: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '8px 0 0', wordBreak: 'break-word', overflowWrap: 'break-word' },

  actionCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 },
  actionTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 },
  actionDesc: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 14px' },
  researchBtn: {
    width: '100%', padding: '10px 16px', borderRadius: 10,
    background: 'linear-gradient(135deg, var(--tf-nav-from), var(--tf-nav-to))', border: 'none',
    color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  rerunBtn: {
    marginLeft: 'auto', padding: '4px 10px', borderRadius: 7, border: '1px solid var(--border)',
    background: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer',
  },
  reextractBtn: {
    width: '100%', padding: '8px 16px', borderRadius: 10, marginBottom: 8,
    background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
    color: '#3b82f6', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  runningBox: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' },
  runningSpinner: {
    width: 22, height: 22, borderRadius: '50%', border: '2px solid var(--border)',
    borderTopColor: 'var(--accent)', animation: 'spin 0.9s linear infinite', flexShrink: 0,
  },
  doneBox: { display: 'flex', alignItems: 'center', gap: 10 },
  errMsg: { fontSize: 11, color: '#b91c1c', background: 'rgba(220,38,38,0.08)', borderRadius: 7, padding: '6px 10px', marginBottom: 10 },
  cooldownBox: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '8px 10px',
  },

  sourcesCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 },
  sourceItem: {
    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
    borderBottom: '1px solid var(--border)', textDecoration: 'none',
  },
  srcIcon: { fontSize: 14, flexShrink: 0, width: 20, textAlign: 'center' },
  srcContent: { flex: 1, minWidth: 0 },
  srcTitle: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 2, lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' },
  srcUrl: { fontSize: 10, color: 'var(--text-dim)', wordBreak: 'break-all' },

  noReport: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '80px 20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
    textAlign: 'center',
  },
};
